import { useState, useEffect } from 'react';
import { Container, Form, Button, Card, Spinner, Alert, Modal, Row, Col } from 'react-bootstrap';
import { supabase, getSafeUser, getCachedSession, getCachedProfile, setCachedProfile } from '../supabaseClient';
import { useNavigate } from 'react-router-dom';
import { toast } from 'react-toastify';
import axios from 'axios';
import NavBar from './Components/Navbar';
import './Account.css';

const Account = () => {
  const navigate = useNavigate();

  const [email, setEmail] = useState('');
  const [fullName, setFullName] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');

  // Change Password
  const [newPassword, setNewPassword] = useState('');
  const [confirmPassword, setConfirmPassword] = useState('');
  const [changingPassword, setChangingPassword] = useState(false);

  // Delete Account
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleting, setDeleting] = useState(false);

  // Subscription Details
  const [profileData, setProfileData] = useState(() => {
    const s = getCachedSession();
    return s?.user?.id ? getCachedProfile(s.user.id) : null;
  });
  const [showCancelModal, setShowCancelModal] = useState(false);
  const [cancellingSubscription, setCancellingSubscription] = useState(false);

  const fetchProfile = async () => {
    setLoading(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        navigate('/auth');
        return;
      }

      const { data: { user }, error: userError } = await getSafeUser(5000);
      if (userError || !user) {
        navigate('/auth');
        return;
      }

      setEmail(user.email);
      setFullName(user.user_metadata?.full_name || '');

      // Fetch extended details from backend profile API
      const response = await axios.get(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/user/profile`,
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.data) {
        setProfileData(response.data);
        setCachedProfile(session.user.id, response.data);
      }
    } catch (err) {
      console.error("Error fetching user profile details:", err);
      toast.error("Could not load user profile details.");
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchProfile();

    const { data: { subscription } } = supabase.auth.onAuthStateChange((event) => {
      if (event === 'SIGNED_OUT') {
        navigate('/auth');
      }
    });

    return () => {
      subscription?.unsubscribe();
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [navigate]);

  const handleSave = async (e) => {
    e.preventDefault();
    setSaving(true);
    setSuccessMessage('');

    const { error } = await supabase.auth.updateUser({
      data: { full_name: fullName }
    });

    if (error) {
      toast.error("Failed to update profile. Please try again.");
    } else {
      setSuccessMessage('Profile updated successfully!');
      toast.success("Profile updated successfully!");
    }

    setSaving(false);
  };

  const handleChangePassword = async (e) => {
    e.preventDefault();
    if (newPassword.length < 6) {
      toast.warning("Password must be at least 6 characters.");
      return;
    }

    if (newPassword !== confirmPassword) {
      toast.warning("Passwords do not match.");
      return;
    }

    setChangingPassword(true);

    const { error } = await supabase.auth.updateUser({
      password: newPassword
    });

    if (error) {
      toast.error("Failed to change password. Please try again.");
    } else {
      toast.success("Password changed successfully!");
      setNewPassword('');
      setConfirmPassword('');
    }

    setChangingPassword(false);
  };

  const handleCancelSubscription = async () => {
    setCancellingSubscription(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("No active session. Please sign in again.");
        navigate('/auth');
        return;
      }

      const response = await axios.post(
        `${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/cancel-subscription`,
        {},
        {
          headers: {
            'Authorization': `Bearer ${session.access_token}`
          }
        }
      );

      if (response.data && response.data.status === 'success') {
        toast.success(response.data.message || "Subscription cancelled successfully!");
        // Refresh local details
        await fetchProfile();
      } else {
        toast.error("Could not complete subscription cancellation.");
      }
    } catch (err) {
      console.error("Cancel subscription error:", err);
      const errMsg = err.response?.data?.detail || "An error occurred while cancelling your subscription.";
      toast.error(errMsg);
    } finally {
      setCancellingSubscription(false);
      setShowCancelModal(false);
    }
  };

  const handleDeleteAccount = async () => {
    setDeleting(true);
    try {
      const { data: { session } } = await supabase.auth.getSession();
      if (!session) {
        toast.error("No active session. Please sign in again.");
        navigate('/auth');
        return;
      }

      const response = await fetch(`${process.env.REACT_APP_API_URL || 'http://localhost:8000'}/delete_account`, {
        method: 'DELETE',
        headers: {
          'Authorization': `Bearer ${session.access_token}`
        }
      });

      const data = await response.json();
      if (!response.ok) {
        throw new Error(data.detail || "Failed to delete account");
      }

      toast.success("Your account has been deleted successfully.");
      await supabase.auth.signOut();
      navigate('/auth');
    } catch (err) {
      console.error("Account deletion error:", err);
      toast.error(err.message || "An error occurred while deleting your account.");
    } finally {
      setDeleting(false);
      setShowDeleteModal(false);
    }
  };

  // UI Formatters
  const formatPlanName = (plan) => {
    if (!plan) return 'Free Starter';
    switch (plan.toLowerCase()) {
      case 'starter':
        return 'Free Starter';
      case 'growth':
        return 'Growth Plan';
      case 'expansion':
        return 'Expansion Plan';
      case 'agency':
        return 'Agency Plan';
      default:
        return plan.charAt(0).toUpperCase() + plan.slice(1);
    }
  };

  const isPaidPlan = (plan) => {
    if (!plan) return false;
    const p = plan.toLowerCase();
    return p === 'growth' || p === 'expansion' || p === 'agency';
  };

  return (
    <>
      <NavBar />
      <Container className="account-page-container">
        <div className="account-header">
          <h1>Account Settings</h1>
          <p>Manage your profile settings, subscription, and security credentials.</p>
        </div>

        {loading ? (
          <div className="loader-container">
            <Spinner animation="border" className="spinner-custom" />
            <span>Retrieving profile information...</span>
          </div>
        ) : (
          <Row className="g-4">
            {/* Personal Details Column */}
            <Col lg={6}>
              <div className="d-flex flex-column gap-4">
                {/* Profile Information */}
                <Card className="settings-card">
                  <div className="settings-card-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M20 21v-2a4 4 0 0 0-4-4H8a4 4 0 0 0-4 4v2" />
                      <circle cx="12" cy="7" r="4" />
                    </svg>
                    Profile Information
                  </div>

                  <Form onSubmit={handleSave}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label-custom">Email Address</Form.Label>
                      <Form.Control type="email" value={email} disabled className="form-control-custom" />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="form-label-custom">Full Name</Form.Label>
                      <Form.Control
                        type="text"
                        value={fullName}
                        onChange={(e) => setFullName(e.target.value)}
                        placeholder="Enter your full name"
                        className="form-control-custom"
                        required
                      />
                    </Form.Group>

                    <Button type="submit" className="w-100 btn-primary-custom" disabled={saving}>
                      {saving && <Spinner size="sm" animation="border" className="me-2" />}
                      {saving ? "Saving Changes..." : "Save Profile Details"}
                    </Button>
                  </Form>
                  {successMessage && (
                    <Alert variant="success" className="mt-3 text-center py-2 small">
                      {successMessage}
                    </Alert>
                  )}
                </Card>

                {/* Change Password */}
                <Card className="settings-card">
                  <div className="settings-card-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
                      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
                    </svg>
                    Change Password
                  </div>

                  <Form onSubmit={handleChangePassword}>
                    <Form.Group className="mb-3">
                      <Form.Label className="form-label-custom">New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Min. 6 characters"
                        value={newPassword}
                        onChange={(e) => setNewPassword(e.target.value)}
                        className="form-control-custom"
                        required
                      />
                    </Form.Group>

                    <Form.Group className="mb-4">
                      <Form.Label className="form-label-custom">Confirm New Password</Form.Label>
                      <Form.Control
                        type="password"
                        placeholder="Verify your new password"
                        value={confirmPassword}
                        onChange={(e) => setConfirmPassword(e.target.value)}
                        className="form-control-custom"
                        required
                      />
                    </Form.Group>

                    <Button type="submit" variant="warning" className="w-100 fw-bold py-2 rounded-3" disabled={changingPassword}>
                      {changingPassword && <Spinner size="sm" animation="border" className="me-2" />}
                      {changingPassword ? "Updating..." : "Change Password"}
                    </Button>
                  </Form>
                </Card>
              </div>
            </Col>

            {/* Subscription & Security Column */}
            <Col lg={6}>
              <div className="d-flex flex-column">
                {/* Subscription Details Card */}
                <Card className="settings-card">
                  <div className="settings-card-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <rect x="2" y="5" width="20" height="14" rx="2" ry="2" />
                      <line x1="2" y1="10" x2="22" y2="10" />
                    </svg>
                    Plan & Subscription details
                  </div>

                  {profileData ? (
                    <div>
                      <div className="subscription-panel">
                        <div className="plan-info-header">
                          <div>
                            <div className="plan-details-title">ACTIVE MEMBERSHIP</div>
                            <div className="plan-title-name">{formatPlanName(profileData.plan)}</div>
                          </div>
                          <span className={`status-badge-custom status-${(profileData.plan_status || 'active').toLowerCase()}`}>
                            {profileData.plan_status ? profileData.plan_status.toUpperCase() : 'ACTIVE'}
                          </span>
                        </div>

                        <div className="meta-list mt-3">
                          {profileData.subscription_start && (
                            <div className="meta-item">
                              <span className="meta-label">Billing Cycle Started</span>
                              <span className="meta-value">{new Date(profileData.subscription_start).toLocaleDateString()}</span>
                            </div>
                          )}
                          <div className="meta-item">
                            <span className="meta-label">Scans used this month</span>
                            <span className="meta-value">{profileData.scans_used}</span>
                          </div>
                          <div className="meta-item">
                            <span className="meta-label">Schema Generations used</span>
                            <span className="meta-value">{profileData.schema_generations_used || 0}</span>
                          </div>
                          <div className="meta-item">
                            <span className="meta-label">Robots.txt Generations used</span>
                            <span className="meta-value">{profileData.robots_generations_used || 0}</span>
                          </div>
                        </div>
                      </div>

                      {isPaidPlan(profileData.plan) ? (
                        <div className="d-flex flex-column gap-2 mt-3">
                          <Button
                            variant="outline-primary"
                            className="w-100 fw-bold py-2 rounded-3"
                            onClick={() => navigate('/pricing')}
                          >
                            Upgrade or Downgrade Plan
                          </Button>
                          <Button
                            variant="outline-danger"
                            className="w-100 fw-bold py-2 rounded-3"
                            onClick={() => setShowCancelModal(true)}
                          >
                            Cancel Active Subscription
                          </Button>
                          <p className="text-muted text-center small mt-1">
                            Note: Cancelling immediately terminates Stripe billing and reverts your profile to the Free tier.
                          </p>
                        </div>
                      ) : (
                        <div className="text-center py-3">
                          <p className="text-muted small">You are on the Free/Starter tier. Upgrade to unlock bulk scans, deeper GSC/GA integrations, and premium tools.</p>
                          <Button
                            variant="outline-primary"
                            className="fw-bold px-4 rounded-3"
                            onClick={() => navigate('/pricing')}
                          >
                            Upgrade Membership
                          </Button>
                        </div>
                      )}
                    </div>
                  ) : (
                    <div className="text-center py-4">
                      <Spinner size="sm" animation="border" className="text-muted me-2" />
                      <span className="text-muted small">Loading payment parameters...</span>
                    </div>
                  )}
                </Card>

                {/* Danger Zone */}
                <Card className="settings-card danger-card">
                  <div className="settings-card-title danger-card-title">
                    <svg width="20" height="20" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round">
                      <path d="M10.29 3.86L1.82 18a2 2 0 0 0 1.71 3h16.94a2 2 0 0 0 1.71-3L13.71 3.86a2 2 0 0 0-3.42 0z" />
                      <line x1="12" y1="9" x2="12" y2="13" />
                      <line x1="12" y1="17" x2="12.01" y2="17" />
                    </svg>
                    Danger Zone
                  </div>

                  <p className="small text-muted" style={{ lineHeight: '1.5' }}>
                    Permanently delete your profile and account information from SeenByAI. This action is final and cannot be recovered.
                  </p>

                  <Button
                    variant="danger"
                    className="w-100 fw-bold py-2 rounded-3 mt-2"
                    onClick={() => setShowDeleteModal(true)}
                    disabled={deleting}
                  >
                    {deleting && <Spinner size="sm" animation="border" className="me-2" />}
                    Delete Account Permanently
                  </Button>
                </Card>
              </div>
            </Col>
          </Row>
        )}
      </Container>

      {/* Subscription Cancellation Modal */}
      <Modal show={showCancelModal} onHide={() => !cancellingSubscription && setShowCancelModal(false)} centered>
        <Modal.Header closeButton={!cancellingSubscription}>
          <Modal.Title className="text-warning">Cancel Subscription ⚠️</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to cancel your premium plan? This action will immediately terminate your active subscription billing in Stripe and reset your SeenByAI profile features to the Starter tier.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowCancelModal(false)} disabled={cancellingSubscription}>
            Keep Subscription
          </Button>
          <Button variant="danger" onClick={handleCancelSubscription} disabled={cancellingSubscription}>
            {cancellingSubscription ? <Spinner size="sm" animation="border" className="me-2" /> : null}
            {cancellingSubscription ? "Processing..." : "Yes, Cancel Subscription"}
          </Button>
        </Modal.Footer>
      </Modal>

      {/* Account Deletion Modal */}
      <Modal show={showDeleteModal} onHide={() => !deleting && setShowDeleteModal(false)} centered>
        <Modal.Header closeButton={!deleting}>
          <Modal.Title className="text-danger">Delete Account ⚠️</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          Are you sure you want to delete your SeenByAI account? This action is permanent and cannot be undone.
          Your profile, configuration settings, and reports will be removed.
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={() => setShowDeleteModal(false)} disabled={deleting}>
            Cancel
          </Button>
          <Button variant="danger" onClick={handleDeleteAccount} disabled={deleting}>
            {deleting ? <Spinner size="sm" animation="border" className="me-2" /> : null}
            {deleting ? "Deleting..." : "Yes, Delete My Account"}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default Account;


